import { supabase } from '../lib/supabase';

/**
 * Retrieves data from selected sources for fine-tuning
 * @param chatbotPublicId Chatbot's public ID
 * @param dataSources Selected data sources
 * @returns Formatted data for fine-tuning
 */
export async function collectFineTuningData(chatbotPublicId: string, dataSources: string[] = []) {
  let trainingData = '';
  
  console.log(`Collecting training data for sources: ${dataSources.join(', ')}`);
  
  try {
    // Get AI settings from chatbot config - we'll use this for multiple sources
    const { data: chatbotConfig } = await supabase
      .from('chatbot_configs')
      .select('ai_settings')
      .eq('chatbot_public_id', chatbotPublicId)
      .single();
    
    console.log('Retrieved chatbot config:', chatbotConfig ? 'success' : 'not found');
    
    let aiSettings = null;
    if (chatbotConfig?.ai_settings) {
      try {
        aiSettings = typeof chatbotConfig.ai_settings === 'string'
          ? JSON.parse(chatbotConfig.ai_settings)
          : chatbotConfig.ai_settings;
      } catch (e) {
        console.error('Error parsing AI settings:', e);
      }
    }
    
    // Get knowledge base data
    if (dataSources.includes('knowledge_base') && aiSettings) {
      try {
        const knowledgeBase = aiSettings.knowledgeBase || [];
        console.log(`Retrieved ${knowledgeBase.length} knowledge base items`);
        
        if (knowledgeBase.length > 0) {
          trainingData += '# KNOWLEDGE BASE\n\n';
          
          for (const item of knowledgeBase) {
            // Safely access properties
            const title = item.name || 'Untitled';
            const content = item.content || '';
            
            if (content) {
              trainingData += `## ${title}\n`;
              trainingData += `${content}\n\n`;
            }
          }
          
          console.log('Added knowledge base content to training data');
        } else {
          console.log('No knowledge base items found');
        }
      } catch (error) {
        console.error('Error processing knowledge base:', error);
      }
    }
    
    // Get prompts and objectives
    if (dataSources.includes('prompts') && aiSettings) {
      try {
        // Extract and format prompts
        if (aiSettings.prompts) {
          trainingData += '# PROMPTS\n\n';
          
          if (aiSettings.prompts.generalPrompt) {
            trainingData += `## General Prompt\n${aiSettings.prompts.generalPrompt}\n\n`;
            console.log('Added general prompt to training data');
          }
          
          if (aiSettings.prompts.attitudePrompt) {
            trainingData += `## Attitude Prompt\n${aiSettings.prompts.attitudePrompt}\n\n`;
            console.log('Added attitude prompt to training data');
          }
        } else {
          console.log('No prompts found in AI settings');
        }
        
        // Extract and format objectives
        if (aiSettings.objectives && Array.isArray(aiSettings.objectives) && aiSettings.objectives.length > 0) {
          trainingData += '# OBJECTIVES\n\n';
          
          for (const objective of aiSettings.objectives) {
            trainingData += `- ${objective}\n`;
          }
          
          trainingData += '\n';
          console.log(`Added ${aiSettings.objectives.length} objectives to training data`);
        } else {
          console.log('No objectives found in AI settings');
        }
      } catch (error) {
        console.error('Error processing prompts and objectives:', error);
      }
    }
    
    // Get conversations
    if (dataSources.includes('conversations')) {
      try {
        // First check if the conversations table exists
        const { error: tableCheckError } = await supabase
          .from('conversations')
          .select('id')
          .limit(1);
        
        if (tableCheckError) {
          console.error('Error checking conversations table:', tableCheckError);
          console.log('Skipping conversations data source due to table access error');
        } else {
          const { data: conversations } = await supabase
            .from('conversations')
            .select(`
              id,
              initial_message_content,
              message_count,
              conversation_messages (
                role,
                content,
                created_at
              )
            `)
            .eq('chatbot_public_id', chatbotPublicId)
            .limit(100); // Limit to 100 conversations to avoid overload
          
          console.log(`Retrieved ${conversations?.length || 0} conversations`);
          
          if (conversations && conversations.length > 0) {
            trainingData += '# CONVERSATIONS\n\n';
            
            for (const conversation of conversations) {
              trainingData += `## Conversation ${conversation.id}\n`;
              
              if (conversation.conversation_messages && conversation.conversation_messages.length > 0) {
                // Sort messages by creation date
                const sortedMessages = [...conversation.conversation_messages].sort(
                  (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
                
                for (const message of sortedMessages) {
                  const role = message.role === 'user' ? 'User' : 'Assistant';
                  trainingData += `${role}: ${message.content}\n`;
                }
                
                trainingData += '\n';
              }
            }
            
            console.log('Added conversation content to training data');
          }
        }
      } catch (error) {
        console.error('Error retrieving conversations:', error);
      }
    }
    
    // Get lead conversations
    if (dataSources.includes('lead_conversations')) {
      try {
        // First check if the table exists
        const { error: tableCheckError } = await supabase
          .from('conversation_contact_info')
          .select('id')
          .limit(1);
          
        if (tableCheckError) {
          console.error('Error checking conversation_contact_info table:', tableCheckError);
          console.log('Skipping lead conversations data source due to table access error');
        } else {
          const { data: leads } = await supabase
            .from('conversation_contact_info')
            .select(`
              conversation_id,
              type,
              value,
              conversations (
                conversation_messages (
                  role,
                  content,
                  created_at
                )
              )
            `)
            .eq('chatbot_public_id', chatbotPublicId);
          
          console.log(`Retrieved ${leads?.length || 0} lead conversations`);
          
          if (leads && leads.length > 0) {
            trainingData += '# LEAD CONVERSATIONS\n\n';
            
            for (const lead of leads) {
              trainingData += `## Lead ${lead.type}: ${lead.value}\n`;
              
              if (lead.conversations?.conversation_messages && lead.conversations.conversation_messages.length > 0) {
                // Sort messages by creation date
                const sortedMessages = [...lead.conversations.conversation_messages].sort(
                  (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
                
                for (const message of sortedMessages) {
                  const role = message.role === 'user' ? 'User' : 'Assistant';
                  trainingData += `${role}: ${message.content}\n`;
                }
                
                trainingData += '\n';
              }
            }
            
            console.log('Added lead conversation content to training data');
          }
        }
      } catch (error) {
        console.error('Error retrieving lead conversations:', error);
      }
    }
    
    // Get products
    if (dataSources.includes('product_content')) {
      try {
        // First check if the products table exists
        const { error: tableCheckError } = await supabase
          .from('products')
          .select('id')
          .limit(1);
          
        if (tableCheckError) {
          console.error('Error checking products table:', tableCheckError);
          console.log('Skipping products data source due to table access error');
        } else {
          const { data: products } = await supabase
            .from('products')
            .select('*')
            .eq('chatbot_public_id', chatbotPublicId);
          
          console.log(`Retrieved ${products?.length || 0} products`);
          
          if (products && products.length > 0) {
            trainingData += '# PRODUCTS\n\n';
            
            for (const product of products) {
              trainingData += `## ${product.name}\n`;
              
              if (product.description) {
                trainingData += `Description: ${product.description}\n`;
              }
              
              trainingData += `Price: ${product.price} ${product.currency}\n`;
              
              if (product.category) {
                trainingData += `Category: ${product.category}\n`;
              }
              
              if (product.tags && product.tags.length > 0) {
                trainingData += `Tags: ${product.tags.join(', ')}\n`;
              }
              
              trainingData += '\n';
            }
            
            console.log('Added product content to training data');
          }
        }
      } catch (error) {
        console.error('Error retrieving products:', error);
      }
    }
    
    // Get real estate properties
    if (dataSources.includes('real_estate')) {
      try {
        // First check if the real estate properties table exists
        const { error: tableCheckError } = await supabase
          .from('realestate_properties')
          .select('id')
          .limit(1);
          
        if (tableCheckError) {
          console.error('Error checking realestate_properties table:', tableCheckError);
          console.log('Skipping real estate data source due to table access error');
        } else {
          const { data: properties } = await supabase
            .from('realestate_properties')
            .select('*')
            .eq('chatbot_public_id', chatbotPublicId);
          
          console.log(`Retrieved ${properties?.length || 0} real estate properties`);
          
          if (properties && properties.length > 0) {
            trainingData += '# REAL ESTATE PROPERTIES\n\n';
            
            for (const property of properties) {
              trainingData += `## ${property.title}\n`;
              
              if (property.description) {
                trainingData += `Description: ${property.description}\n`;
              }
              
              if (property.price) {
                trainingData += `Price: ${property.price}\n`;
              }
              
              if (property.location) {
                trainingData += `Location: ${property.location}\n`;
              }
              
              if (property.surface) {
                trainingData += `Area: ${property.surface} m²\n`;
              }
              
              if (property.rooms) {
                trainingData += `Rooms: ${property.rooms}\n`;
              }
              
              if (property.features && property.features.length > 0) {
                trainingData += `Features: ${property.features.join(', ')}\n`;
              }
              
              trainingData += '\n';
            }
            
            console.log('Added real estate content to training data');
          }
        }
      } catch (error) {
        console.error('Error retrieving real estate properties:', error);
      }
    }
    
    // Get services
    if (dataSources.includes('services')) {
      try {
        // First check if the services table exists
        const { error: tableCheckError } = await supabase
          .from('services')
          .select('id')
          .limit(1);
          
        if (tableCheckError) {
          console.error('Error checking services table:', tableCheckError);
          console.log('Skipping services data source due to table access error');
        } else {
          const { data: services } = await supabase
            .from('services')
            .select('*')
            .eq('chatbot_public_id', chatbotPublicId);
          
          console.log(`Retrieved ${services?.length || 0} services`);
          
          if (services && services.length > 0) {
            trainingData += '# SERVICES\n\n';
            
            for (const service of services) {
              trainingData += `## ${service.title}\n`;
              
              if (service.subtitle) {
                trainingData += `${service.subtitle}\n`;
              }
              
              if (service.content) {
                trainingData += `${service.content}\n`;
              }
              
              trainingData += '\n';
            }
            
            console.log('Added services content to training data');
          }
        }
      } catch (error) {
        console.error('Error retrieving services:', error);
      }
    }
    
    console.log(`Total training data size: ${trainingData.length} characters`);
    return trainingData;
  } catch (error) {
    console.error('Error collecting training data:', error);
    return `# ERROR COLLECTING DATA\nAn error occurred while collecting the training data: ${error instanceof Error ? error.message : 'Unknown error'}`;
  }
}

/**
 * Creates a training file for fine-tuning with retrieved data
 * @param chatbotPublicId Chatbot's public ID
 * @param dataSources Selected data sources
 * @returns Created file ID
 */
export async function createFineTuningFile(chatbotPublicId: string, dataSources: string[] = []) {
  try {
    const trainingData = await collectFineTuningData(chatbotPublicId, dataSources);
    
    if (trainingData.length < 100) {
      return {
        fileId: null,
        success: false,
        error: 'Not enough training data. Please add more content to your selected sources.',
        dataSize: trainingData.length
      };
    }
    
    // Here, you would typically call your API to create a training file
    // This depends on your backend implementation
    
    // For now, we assume it works and return a dummy ID
    return {
      fileId: `ft_${Date.now()}`,
      success: true,
      dataSize: trainingData.length
    };
  } catch (error) {
    console.error('Error creating fine-tuning file:', error);
    return {
      fileId: null,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      dataSize: 0
    };
  }
} 